import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { TripType } from "@/components/forms/types";
import { TransportVoucherData } from "@/types/navigation";

// Styles pour le PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 30,
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 0,
    borderBottom: "1px solid #C9A76A",
    paddingBottom: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#15233D",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 5,
    marginTop: 15,
    color: "#C9A76A",
  },
  section: {
    marginBottom: 10,
  },
  row: {
    flexDirection: "row",
    marginBottom: 5,
  },
  label: {
    width: "40%",
    fontWeight: "bold",
    fontSize: 10,
  },
  value: {
    width: "60%",
    fontSize: 10,
  },
  footer: {
    marginTop: 30,
    borderTop: "1px solid #C9A76A",
    paddingTop: 10,
    fontSize: 8,
    color: "#666666",
    textAlign: "center",
  },
  bonCommande: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#15233D",
    textAlign: "right",
  },
  priceSection: {
    marginTop: 20,
    borderTop: "1px solid #C9A76A",
    paddingTop: 10,
  },
  priceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  priceLabel: {
    fontWeight: "bold",
    fontSize: 10,
  },
  priceValue: {
    fontSize: 10,
    textAlign: "right",
  },
  totalPrice: {
    fontWeight: "bold",
    fontSize: 12,
    color: "#15233D",
  },
});

// Composant pour le bon de transport en PDF
export const TransportVoucherPDF = ({
  data,
}: {
  data: TransportVoucherData;
}) => {
  // Calculer les prix HT et TTC
  const prixTTC = data.estimatedPrice || 0;
  const isExonere = !data.selectedTVA;
  const isModified = data.isModified;

  // Formater les dates
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Obtenir le libellé du type de trajet
  const getTripTypeLabel = (type: TripType) => {
    const labels: Record<TripType, string> = {
      "one-way": "Aller simple",
      "round-trip": "Aller-retour",
      "multi-trip": "Multi-trajets",
      disposition: "Mise à disposition",
    };
    return labels[type] || type;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* En-tête */}
        <View style={styles.header}>
          <Text style={styles.title}>BON DE TRANSPORT VTC</Text>
          <Text style={styles.bonCommande}>
            N° {data.bonCommande || "Non défini"}
          </Text>
        </View>

        {/* Informations sur l'entreprise */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>
            Informations sur l&apos;entreprise
          </Text>
          <View style={styles.row}>
            <Text style={styles.label}>Entreprise:</Text>
            <Text style={styles.value}>{data.entreprise}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>SIREN:</Text>
            <Text style={styles.value}>{data.siren}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>N° Registre VTC:</Text>
            <Text style={styles.value}>{data.registreVTC}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Téléphone:</Text>
            <Text style={styles.value}>{data.telPro}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Email:</Text>
            <Text style={styles.value}>{data.mailPro}</Text>
          </View>
        </View>

        {/* Informations sur le client */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>Informations sur le client</Text>
          {data.isEntreprise && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Entreprise:</Text>
                <Text style={styles.value}>{data.entrepriseName}</Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>SIRET:</Text>
                <Text style={styles.value}>{data.siret}</Text>
              </View>
            </>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>Nom et prénom:</Text>
            <Text style={styles.value}>
              {data.firstName} {data.lastName}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Téléphone:</Text>
            <Text style={styles.value}>{data.phone}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Email:</Text>
            <Text style={styles.value}>{data.email}</Text>
          </View>
        </View>

        {/* Détails de la réservation */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>Détails de la réservation</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Type de course:</Text>
            <Text style={styles.value}>{getTripTypeLabel(data.tripType)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Date de prise en charge:</Text>
            <Text style={styles.value}>{formatDate(data.date)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Heure de prise en charge:</Text>
            <Text style={styles.value}>{data.time}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Lieu de prise en charge:</Text>
            <Text style={styles.value}>{data.departure}</Text>
          </View>
          {data.tripType !== "disposition" && (
            <View style={styles.row}>
              <Text style={styles.label}>Destination:</Text>
              <Text style={styles.value}>{data.arrival}</Text>
            </View>
          )}
          {data.tripType === "disposition" && (
            <View style={styles.row}>
              <Text style={styles.label}>Durée de mise à disposition:</Text>
              <Text style={styles.value}>{data.dispositionDuration}h</Text>
            </View>
          )}
          {data.tripType === "round-trip" && (
            <>
              <View style={styles.row}>
                <Text style={styles.label}>Date de retour:</Text>
                <Text style={styles.value}>
                  {formatDate(data.returnDate || "")}
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>Heure de retour:</Text>
                <Text style={styles.value}>{data.returnTime}</Text>
              </View>
              {data.customizeReturn && (
                <View style={styles.row}>
                  <Text style={styles.label}>Adresse de retour:</Text>
                  <Text style={styles.value}>{data.returnArrival}</Text>
                </View>
              )}
            </>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>Nombre de passagers:</Text>
            <Text style={styles.value}>{data.passengers}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Nombre de bagages:</Text>
            <Text style={styles.value}>{data.luggage}</Text>
          </View>
          {data.babySeats > 0 && (
            <View style={styles.row}>
              <Text style={styles.label}>Sièges bébé:</Text>
              <Text style={styles.value}>{data.babySeats}</Text>
            </View>
          )}
          {data.childSeats > 0 && (
            <View style={styles.row}>
              <Text style={styles.label}>Sièges enfant:</Text>
              <Text style={styles.value}>{data.childSeats}</Text>
            </View>
          )}
        </View>

        {/* Informations sur le véhicule et le chauffeur */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>
            Informations sur le véhicule et le chauffeur
          </Text>
          <View style={styles.row}>
            <Text style={styles.label}>Chauffeur:</Text>
            <Text style={styles.value}>{data.chauffeur}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Véhicule:</Text>
            <Text style={styles.value}>{data.vehicule}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Type de véhicule:</Text>
            <Text style={styles.value}>{data.modele}</Text>
          </View>
          {data.vehicleClassLabel && (
            <View style={styles.row}>
              <Text style={styles.label}>Classe véhicule:</Text>
              <Text style={styles.value}>{data.vehicleClassLabel}</Text>
            </View>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>Immatriculation:</Text>
            <Text style={styles.value}>{data.immatriculation}</Text>
          </View>
          {data.maxPassengers && (
            <View style={styles.row}>
              <Text style={styles.label}>Capacité maximale:</Text>
              <Text style={styles.value}>
                {data.maxPassengers} passagers, {data.maxLuggage} bagages
              </Text>
            </View>
          )}
        </View>

        {/* Détails tarifaires */}
        <View style={styles.priceSection}>
          <Text style={styles.subtitle}>Détails tarifaires</Text>
          {isExonere ? (
            <>
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>Total:</Text>
                <Text style={[styles.priceValue, styles.totalPrice]}>
                  {prixTTC.toFixed(2)}€
                </Text>
              </View>
              <View style={styles.row}>
                <Text style={styles.label}>TVA:</Text>
                <Text style={styles.value}>Exonéré</Text>
              </View>
            </>
          ) : (
            <>
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>Total HT:</Text>
                <Text style={styles.priceValue}>
                  {(
                    prixTTC /
                    (1 + parseFloat(data.selectedTVA || "0") / 100)
                  ).toFixed(2)}
                  €
                </Text>
              </View>
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>
                  TVA ({data.selectedTVA}%):
                </Text>
                <Text style={styles.priceValue}>
                  {(
                    prixTTC -
                    prixTTC / (1 + parseFloat(data.selectedTVA || "0") / 100)
                  ).toFixed(2)}
                  €
                </Text>
              </View>
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>Total TTC:</Text>
                <Text style={[styles.priceValue, styles.totalPrice]}>
                  {prixTTC.toFixed(2)}€
                </Text>
              </View>
            </>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>Mode de paiement:</Text>
            <Text style={styles.value}>{data.modePaiement}</Text>
          </View>
        </View>

        {/* Pied de page */}
        <View style={styles.footer}>
          <Text>
            {!isModified &&
              "Ce bon de transport est généré automatiquement à partir des informations de réservation. Document non contractuel, le prix final peut varier en fonction des conditions réelles du trajet."}
            {isExonere && "\nTVA non applicable - article 293 B du CGI"}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
